import { SafeError, SafePromise } from '@benzinga/safe-await';
import { Financials, Ownership, Peer, ValuationRatio, EarningReport } from './entities';

interface RestfulResult<DATA> {
  result: DATA;
}

interface RestfulOwnershipResult {
  result?: {
    summary: Ownership[];
  };
}

interface RestfulPeersResult {
  instruments: Peer[];
}

export type IncomingSecuritiesResult = RestfulResult<Financials[]>;

export type IncomingOwnershipResult = RestfulOwnershipResult;

export type IncomingPeersResult = RestfulPeersResult;

export type IncomingValuationRatiosResult = RestfulResult<ValuationRatio[]>;

export type IncomingOperationRatiosResult = RestfulResult<Financials[]>;

//export type IncomingOperationRatiosResult = RestfulResult<OperationRatio[]>;

export type IncomingEarningsReportResult = RestfulResult<EarningReport[]>;

export const ingestSecurities = async (
  financialsData: SafePromise<IncomingSecuritiesResult>,
): SafePromise<Financials[]> => {
  const rawSecurities = await financialsData;

  if (rawSecurities.err) {
    return { err: rawSecurities.err };
  } else {
    if (rawSecurities.ok.result) {
      return { ok: rawSecurities.ok.result };
    } else {
      return { err: new SafeError('did not get a valid financial', 'invalid_financial') };
    }
  }
};

export const ingestOwnership = async (ownershipData: SafePromise<IncomingOwnershipResult>): SafePromise<Ownership> => {
  const rawOwnership = await ownershipData;

  if (rawOwnership.err) {
    return { err: rawOwnership.err };
  } else {
    if (rawOwnership.ok?.result?.[0]?.summary?.[0]) {
      return { ok: rawOwnership.ok?.result?.[0]?.summary?.[0] };
    } else {
      return { err: new SafeError('did not get a valid ownership', 'invalid_ownership') };
    }
  }
};

export const ingestPeers = async (peersData: SafePromise<IncomingPeersResult>): SafePromise<Peer[]> => {
  const rawPeers = await peersData;
  if (rawPeers.err) {
    return { err: rawPeers.err };
  } else {
    if (rawPeers.ok?.instruments) {
      return { ok: rawPeers.ok.instruments };
    }
    return { err: new SafeError('did not get a valid peer', 'invalid_peer') };
  }
};


export const ingestValuationRatios = async (
  valuationRatiosData: SafePromise<IncomingOperationRatiosResult>,
): SafePromise<Financials[]> => {
  const rawValuationRatios = await valuationRatiosData;

  if (rawValuationRatios.err) {
    return { err: rawValuationRatios.err };
  } else {
    if (rawValuationRatios.ok.result) {
      return { ok: rawValuationRatios.ok.result };
    } else {
      return { err: new SafeError('did not get a valid valuation ratio', 'invalid_valuation_ratio') };
    }
  }
};

export const ingestOperationRatios = async (
  operationRatiosData: SafePromise<IncomingOperationRatiosResult>,
): SafePromise<Financials[]> => {
  const rawOperationRatios = await operationRatiosData;

  if (rawOperationRatios.err) {
    return { err: rawOperationRatios.err };
  } else {
    if (rawOperationRatios.ok.result) {
      return { ok: rawOperationRatios.ok.result };
    } else {
      return { err: new SafeError('did not get a valid operation ratio', 'invalid_operation_ratio') };
    }
  }
};

export const ingestEarningsReports = async (
  earningsReportsData: SafePromise<IncomingOperationRatiosResult>,
): SafePromise<Financials[]> => {
  const rawEarningsReports = await earningsReportsData;

  if (rawEarningsReports.err) {
    return { err: rawEarningsReports.err };
  } else {
    if (rawEarningsReports.ok.result) {
      return { ok: rawEarningsReports.ok.result };
    } else {
      return { err: new SafeError('did not get a valid earnings report', 'invalid_earnings_report') };
    }
  }
};
